<div class="h-full flex flex-col bg-gray-100 rounded-2xl p-4">
  @if (user(); as u) {
    <!-- Header with back button -->
    <div class="py-2 px-6 mb-6">
      <div class="flex items-center gap-4 mb-4">
        <button class="p-2 rounded-md hover:bg-gray-200 transition-colors" (click)="goBack()">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
        </button>
        <h2 class="text-3xl font-bold">User Details</h2>
      </div>
    </div>

    <div class="flex-1 overflow-auto space-y-6">
      <!-- User Information -->
      <div class="bg-white rounded-2xl p-6 shadow-md space-y-6">
        @if (!isEditing()) {
          <!-- View: avatar + name as title, Edit (and Delete operator) on the right -->
          <div class="flex items-center justify-between gap-4">
            <div class="flex items-center gap-3 min-w-0">
              <app-avatar size="lg" [src]="u.avatarThumbnailUrl" [name]="u.displayName" [seed]="u.id" />
              <h3 class="text-3xl font-bold truncate">{{ u.displayName }}</h3>
            </div>
            <div class="flex shrink-0 gap-2">
              @if (canDelete()) {
                <button
                  type="button"
                  class="px-4 py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700 transition-colors"
                  (click)="askDelete()"
                >
                  Delete operator
                </button>
              }
              <button
                type="button"
                class="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
                (click)="startEdit()"
              >
                Edit
              </button>
            </div>
          </div>
        } @else {
          <!-- Edit: avatar picker + name input as title, Cancel / Save on the right -->
          <div>
            <div class="flex items-center gap-3">
              <app-avatar-picker
                [currentUrl]="u.avatarThumbnailUrl"
                [name]="nameControl.value"
                [seed]="u.id"
                [disabled]="isSaving()"
                [(file)]="avatarFile"
              />
              <input
                type="text"
                [formControl]="nameControl"
                (keydown.enter)="save()"
                (keydown.escape)="cancelEdit()"
                class="flex-1 min-w-0 text-3xl font-bold rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 transition-colors focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
                placeholder="Name"
                aria-label="Name"
              />
              <div class="flex gap-2">
                <button
                  type="button"
                  (click)="cancelEdit()"
                  [disabled]="isSaving()"
                  class="px-4 py-2 bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 font-medium rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  (click)="save()"
                  [disabled]="nameControl.invalid || isSaving()"
                  class="px-4 py-2 bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
                >
                  {{ isSaving() ? 'Saving…' : 'Save' }}
                </button>
              </div>
            </div>
            @if (avatarFile()) {
              <div class="text-sm text-gray-500 mt-1">New avatar will be saved when you click Save.</div>
            }
            @if (nameControl.touched && nameControl.invalid) {
              <div class="text-sm text-red-600 mt-1">Name is required.</div>
            }
            @if (errorMessage(); as message) {
              <div class="text-sm text-red-600 mt-1">{{ message }}</div>
            }
          </div>
        }

        <div class="space-y-4">
          <div>
            <span class="font-semibold">Email:</span>
            {{ u.email }}
          </div>

          <div>
            <span class="font-semibold">Phone:</span>
            {{ u.phoneNumber || '—' }}
          </div>

          <div>
            <span class="font-semibold">Role:</span>
            <span class="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {{ u.role }}
            </span>
          </div>

          <div>
            <span class="font-semibold">Status:</span>
            @if (u.isActive) {
              <span class="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Active
              </span>
            } @else {
              <span class="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                Inactive
              </span>
            }
          </div>
        </div>
      </div>

      <!-- Orders only for customers -->
      @if (u.role === 'user') {
        <app-orders [userId]="u.id" />
      }
    </div>

    @if (confirmingDelete()) {
      <app-confirm-dialog
        [busy]="deleting()"
        busyLabel="Deleting…"
        [error]="deleteError()"
        (confirmed)="confirmDelete()"
        (cancelled)="cancelDelete()"
      >
        <ng-container dialogTitle>Delete operator {{ u.displayName }}?</ng-container>
        {{ u.email }} will lose access and their account will be
        <span class="font-semibold text-red-600">permanently deleted</span>.
        This action cannot be undone.
      </app-confirm-dialog>
    }
  } @else {
    <div class="flex items-center justify-center h-full">
      <div class="text-center text-gray-500">
        <div class="text-xl mb-2">Loading user details...</div>
      </div>
    </div>
  }
</div>
